import { useEffect, useMemo, useState } from "react";
import { GameShell, useLiveMode } from "@/components/GameShell";
import { FinishScreen } from "@/components/FinishScreen";
import { QUOTES, pick, shuffle, splitQuote, quotesByPack, type PackId } from "@/data/quotes";
import { ANSWER_LETTERS, ROUND_COUNT, loadBest, saveBest, loadStreak, saveStreak } from "@/lib/game-utils";

interface Round { quoteId: string; first: string; correct: string; options: string[]; }

function buildRounds(pack: PackId): Round[] {
  const pool = quotesByPack(pack).filter((q) => q.text.split(/\s+/).length >= 6);
  const longEnough = pool.length >= ROUND_COUNT ? pool : QUOTES.filter((q) => q.text.split(/\s+/).length >= 6);
  return pick(longEnough, ROUND_COUNT).map((q) => {
    const { first, second } = splitQuote(q.text);
    const distractors: string[] = [];
    const others = shuffle(longEnough.filter((x) => x.id !== q.id));
    for (const p of others) {
      const { second: s2 } = splitQuote(p.text);
      if (s2 && s2 !== second && !distractors.includes(s2)) distractors.push(s2);
      if (distractors.length === 3) break;
    }
    return { quoteId: q.id, first, correct: second, options: shuffle([second, ...distractors]) };
  });
}

export function FinishQuoteGame({ pack = "all" }: { pack?: PackId }) {
  const live = useLiveMode();
  const [rounds, setRounds] = useState<Round[]>(() => buildRounds(pack));
  const [idx, setIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [best, setBest] = useState(0);
  useEffect(() => { setBest(loadBest("finish-quote")); setBestStreak(loadStreak("finish-quote")); }, []);

  const round = rounds[idx];
  const quote = useMemo(() => QUOTES.find((q) => q.id === round?.quoteId)!, [round]);

  const answer = (opt: string) => {
    if (picked) return;
    setPicked(opt);
    if (opt === round.correct) {
      setScore((s) => s + 1);
      setStreak((s) => { const n = s + 1; if (n > bestStreak) setBestStreak(saveStreak("finish-quote", n)); return n; });
    } else setStreak(0);
  };
  const next = () => {
    if (idx + 1 >= rounds.length) { setBest(saveBest("finish-quote", score)); setDone(true); }
    else { setIdx(idx + 1); setPicked(null); }
  };
  const restart = () => { setRounds(buildRounds(pack)); setIdx(0); setScore(0); setStreak(0); setPicked(null); setDone(false); };

  if (done) return <FinishScreen score={score} total={rounds.length} best={best} bestStreak={bestStreak} onRestart={restart} title="Finish the Quote" numeral="II" hubSlug="finish-quote" />;

  return (
    <GameShell title="Finish the Quote" numeral="II" score={score} round={idx + 1} total={rounds.length} streak={streak} timeLimit={25} timeKey={idx} onTimeUp={() => !picked && setPicked("__timeout__")} hubSlug="finish-quote">
      <article className="rounded-2xl border border-border bg-card p-8 sm:p-12 shadow-elegant grain animate-reveal">
        <div className="quote-mark" />
        <p className={`font-display leading-tight ${live ? "text-3xl sm:text-5xl" : "text-2xl sm:text-3xl"}`}>
          {round.first} <span className="text-gold/40">…</span>
        </p>
        <div className="mt-5 text-sm text-muted-foreground">— {quote.author}{quote.era ? ` · ${quote.era}` : ""}</div>
      </article>

      <div className="mt-6 grid gap-3">
        {round.options.map((opt, i) => {
          const correct = picked && opt === round.correct;
          const wrong = picked === opt && opt !== round.correct;
          return (
            <button key={opt + i} onClick={() => answer(opt)} disabled={!!picked}
              className={`flex items-start gap-4 rounded-xl border p-4 text-left transition ${
                correct ? "border-success bg-success/10" : wrong ? "border-error bg-error/10 animate-shake" : picked ? "border-border opacity-50" : "border-border hover:border-gold hover:bg-accent"
              }`}>
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg font-mono font-bold ${correct ? "bg-success text-ink" : wrong ? "bg-error text-cream" : "bg-secondary text-gold"}`}>{ANSWER_LETTERS[i]}</span>
              <span className={`font-display ${live ? "text-xl sm:text-2xl" : "text-lg"} leading-snug`}>… {opt}</span>
            </button>
          );
        })}
      </div>

      {picked && (
        <div className="mt-6 flex items-center justify-between rounded-xl border border-border bg-card p-4">
          <div className="text-sm">
            {picked === round.correct ? <span className="text-success font-medium">✓ Right line.</span> : <span className="text-error font-medium">✗ Real ending: <strong className="text-gold">{round.correct}</strong></span>}
          </div>
          <button onClick={next} className="rounded-full bg-gradient-gold px-5 py-2 text-sm font-semibold text-ink">{idx + 1 >= rounds.length ? "See results" : "Next →"}</button>
        </div>
      )}
    </GameShell>
  );
}
